import { Injectable } from '@angular/core';
import { AdditiveCapabilities } from '@core/access-control/additive-capabilities';
import { AuthenticationToken } from '@core/authentication/authentication-token';
import { AssetClassValueObject } from '@core/models/asset-class-value-object';
import { Company, CompanyType } from '@core/models/company.model';
import { NotificationsCounters } from '@core/models/notification.model';
import { PrivilegeResource } from '@core/models/privileges.model';
import { UserOverview } from '@core/models/user-overview.model';
import { UserProfile } from '@core/models/user-profile.model';
import { User } from '@core/models/user.model';
import { ValueObject } from '@core/models/value-object.model';
import { ListViewType } from '@core/views/list-view-type';
import { ViewType } from '@core/views/view-type';
import { filterNil, Query } from '@datorama/akita';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { UserDetails } from '../models/user-details.model';

import { AppState, AppStore, DetailContext } from './app-state.store';

@Injectable({ providedIn: 'root' })
export class AppStoreQuery extends Query<AppState> {
  constructor(protected store: AppStore) {
    super(store);
  }

  public getAuthenticationToken(): string {
    return this.store.getValue().authentication.token;
  }

  public getValue(): AppState {
    // operate on cloned object
    const state: AppState = Object.assign({}, super.getValue());

    // recreate as it is deserialized as a plain object
    state.user = User.create(state.user);

    return state;
  }

  public selectAuthentication(): Observable<AuthenticationToken> {
    return this.select((state) => state.authentication);
  }

  public selectAvailableAssetClasses(
    parentCodename: AssetClassValueObject['codename'] = null,
  ): Observable<AssetClassValueObject[]> {
    return this.select((state) => state.availableAssetClasses).pipe(
      filterNil,
      map((assetClasses) => assetClasses.filter((assetClass) => assetClass.parent === parentCodename)),
    );
  }

  public selectAvailableCompanies(type: CompanyType = null): Observable<Company[]> {
    if (type) {
      return this.select((state) => state.availableCompanies.filter((company) => company.type === type));
    }

    return this.select((state) => state.availableCompanies);
  }

  public selectAvailableCountries(): Observable<ValueObject[]> {
    return this.select((state) => state.availableCountries);
  }

  public selectAvailableEsgComplianceOptions(): Observable<ValueObject[]> {
    return this.select((state) => state.availableEsgComplianceOptions);
  }

  public selectAvailableIndustries(): Observable<ValueObject[]> {
    return this.select((state) => state.availableIndustries);
  }

  public selectAvailableInvestorClasses(): Observable<ValueObject[]> {
    return this.select((state) => state.availableInvestorClasses);
  }

  public selectAvailableClientClasses(): Observable<ValueObject[]> {
    return this.select((state) => state.availableClientClasses);
  }

  public selectAvailableLanguages(): Observable<ValueObject[]> {
    return this.select((state) => state.availableLanguages);
  }

  public selectAvailableRatings(): Observable<ValueObject[]> {
    return this.select((state) => state.availableRatings);
  }

  public selectAvailableSalutations(): Observable<ValueObject[]> {
    return this.select((state) => state.availableSalutations);
  }

  public selectAvailableTitles(): Observable<ValueObject[]> {
    return this.select((state) => state.availableTitles);
  }

  public selectAvailableOccupations(): Observable<ValueObject[]> {
    return this.select((state) => state.availableOccupations);
  }

  public selectAvailableBusinessTypes(): Observable<ValueObject[]> {
    return this.select((state) => state.availableBusinessTypes);
  }
  public selectAvailableMainSourceOfFunds(): Observable<ValueObject[]> {
    return this.select((state) => state.availableMainSourceOfFunds);
  }

  public selectCapabilities(resource: PrivilegeResource): Observable<AdditiveCapabilities> {
    return this.select((state) => state.privileges?.getCapabilities && state.privileges?.getCapabilities(resource));
  }

  public selectCurrentDetailContext(): Observable<DetailContext> {
    return this.select((state) => state.currentDetailContext);
  }

  public selectCurrentReturnUrl(): Observable<string> {
    return this.select((state) => state.currentReturnUrl);
  }

  public selectCurrentRoute(): Observable<string[]> {
    return this.select((state) => state.currentRoute);
  }

  public selectNotificationsCounters(): Observable<NotificationsCounters> {
    return this.select((state) => state.notificationsCounters);
  }

  public selectUser(): Observable<User> {
    // recreate as it is deserialized as a plain object

    return this.select((state) => User.create(state.user));
  }

  public selectUserProfile(): Observable<UserProfile> {
    return this.select((state) => state.userProfile);
  }

  public selectUserDetails(): Observable<UserDetails> {
    return this.select((state) => state.userDetails);
  }

  public selectUserOverview(id: User['id']): Observable<UserOverview> {
    return this.select((state) => state.usersOverview).pipe(
      map((usersOverview) => usersOverview?.find((uo) => uo.id === id)),
    );
  }

  public selectUsersOverview(): Observable<UserOverview[]> {
    return this.select((state) => state.usersOverview);
  }

  public selectListViewType(): Observable<ListViewType> {
    return this.select((state) => state.listViewType);
  }

  public selectViewType(): Observable<ViewType> {
    return this.select((state) => state.viewType);
  }
}
